import axios from "axios";
import { v4 as uuidv4 } from "uuid";

// ─── Config ───────────────────────────────────────────────────────────────────

export interface S3ServiceConfig {
    /** CDN base URL, e.g. "https://srm-sbox-cdn.a4b.io" */
    cdnUrl: string;
    /** Presign URL base – filename will be appended, e.g. ".../generate-s3-presigned-url?filename=" */
    presignBaseUrl: string;
    /** Returns auth headers for the presign API call */
    getAuthHeaders: () => Record<string, string>;
    /** Optional: rewrite the presign request URL before sending */
    transformPresignUrl?: (url: string) => string;
    /** Optional: rewrite a CDN read URL before fetching */
    transformReadUrl?: (url: string) => string;
    /** Optional: rewrite an S3 upload URL before uploading */
    transformUploadUrl?: (url: string) => string;
}

// ─── Service interface ────────────────────────────────────────────────────────

export interface S3StaticUploadService {
    /** Build the static CDN URL for a given file key (synchronous, no network call). */
    createStaticCdnUrl: (fileName: string) => string;
    /** Upload a browser File to S3 under a unique key and return its CDN URL. */
    createDynamicCdnUrl: (file: File) => Promise<string>;
    /** Fetch the content at dynamicUrl and overwrite the file at staticUrl with it. */
    overrideStaticWithDynamic: (staticUrl: string, dynamicUrl: string) => Promise<void>;
}

// ─── Factory ──────────────────────────────────────────────────────────────────

export const createS3StaticUploadService = (config: S3ServiceConfig): S3StaticUploadService => {
    const {
        cdnUrl,
        presignBaseUrl,
        getAuthHeaders,
        transformPresignUrl,
        transformReadUrl,
        transformUploadUrl,
    } = config;

    const getPresignedUrl = async (fileName: string): Promise<string> => {
        const rawUrl = `${presignBaseUrl}${encodeURIComponent(fileName)}`;
        const requestUrl = transformPresignUrl ? transformPresignUrl(rawUrl) : rawUrl;
        const response = await axios.get(requestUrl, { headers: getAuthHeaders() });
        const url = response?.data?.data?.url;

        if (!url || typeof url !== "string") {
            throw new Error("Failed to generate presigned url");
        }

        return url;
    };

    return {
        createStaticCdnUrl: (fileName: string): string => {
            return `${cdnUrl}/${fileName}`;
        },

        createDynamicCdnUrl: async (file: File): Promise<string> => {
            const dotIndex = file.name.lastIndexOf(".");
            const ext = dotIndex >= 0 ? file.name.slice(dotIndex + 1) : "";
            const key = ext ? `${uuidv4()}.${ext}` : uuidv4();

            const presignedUrl = await getPresignedUrl(key);
            const uploadUrl = transformUploadUrl ? transformUploadUrl(presignedUrl) : presignedUrl;

            await axios.put(uploadUrl, file, {
                headers: {
                    "Content-Type": file.type || "application/octet-stream",
                },
            });

            return `${cdnUrl}/${key}`;
        },

        overrideStaticWithDynamic: async (staticUrl: string, dynamicUrl: string): Promise<void> => {
            const readUrl = transformReadUrl ? transformReadUrl(dynamicUrl) : dynamicUrl;

            const response = await axios.get<ArrayBuffer>(readUrl, {
                responseType: "arraybuffer",
                params: { cache_bust: Date.now() },
            });

            const contentType =
                (response.headers as Record<string, string>)["content-type"] || "application/octet-stream";

            const cdnPrefix = cdnUrl.endsWith("/") ? cdnUrl : `${cdnUrl}/`;
            const staticKey = staticUrl.startsWith(cdnPrefix)
                ? staticUrl.slice(cdnPrefix.length).split("?")[0]
                : staticUrl.split("?")[0];

            const presignedUrl = await getPresignedUrl(staticKey);
            const uploadUrl = transformUploadUrl ? transformUploadUrl(presignedUrl) : presignedUrl;

            await axios.put(uploadUrl, response.data, {
                headers: {
                    "Content-Type": contentType,
                },
            });
        },
    };
};
